import { useEffect, useRef } from 'react'
import { useTheme } from '@/hooks/useTheme'
import { cssVar } from '@/utils/cssVar'
import { fontReady, prefersReducedMotion } from './engines/fontReady'
import {
  MimirConstellation,
  type ConstellationHandle,
  type ConstellationCenter,
} from './engines/constellation'

interface RuneConstellationProps {
  glyph?: string
  count?: number
  center?: ConstellationCenter
  scale?: number
  coreFraction?: number
  mouse?: { radius: number; push: number }
  ambientCount?: number
  className?: string
}

/**
 * The glowing runic ᛗ constellation. Nodes fade in with a fast staggered
 * spawn, then breathe in place. Decorative: skipped entirely under
 * reduced-motion. Recreates with the active palette on theme change and
 * tears the engine down on unmount.
 */
export function RuneConstellation({
  glyph = 'ᛗ',
  count,
  center,
  scale,
  coreFraction,
  mouse,
  ambientCount,
  className,
}: RuneConstellationProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const handleRef = useRef<ConstellationHandle | null>(null)
  const { dark } = useTheme()

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas || prefersReducedMotion()) return

    let disposed = false
    void fontReady([
      { font: "700 64px 'Noto Sans Runic'", text: glyph },
      "64px 'Cardo'",
    ]).then(() => {
      if (disposed || !canvasRef.current) return
      handleRef.current = MimirConstellation(canvasRef.current, {
        coreGlyph: glyph,
        colors: {
          core: cssVar('--art-rune-core'),
          dim: cssVar('--art-rune-dim'),
          link: cssVar('--art-rune-link'),
        },
        count,
        coreCenter: center,
        coreScale: scale,
        coreFraction,
        mouseRadius: mouse?.radius,
        mousePush: mouse?.push,
        ambientCount,
      })
    })

    return () => {
      disposed = true
      handleRef.current?.destroy()
      handleRef.current = null
    }
  }, [glyph, count, center, scale, coreFraction, mouse, ambientCount, dark])

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className={['absolute inset-0 h-full w-full', className].filter(Boolean).join(' ')}
    />
  )
}
